import { DatasetField, getDataset } from './datasets'

export interface JoinDef {
  id: string
  /** Base dataset the join extends */
  baseId: string
  /** Dataset being joined in */
  joinedId: string
  /** Display name, e.g. "Appointments + Test Results" */
  name: string
  description: string
  /** Key on the base rows */
  baseKey: string
  /** Key on the joined rows */
  joinedKey: string
}

/**
 * Curated join relationships — modeled after how a real BI backend would
 * expose "blessed" dataset pairs with predefined keys, rather than
 * free-form joins. All joins are LEFT joins: every base row is kept,
 * unmatched joined fields show as "—".
 */
export const joins: JoinDef[] = [
  {
    id: 'appts-results',
    baseId: 'appointments',
    joinedId: 'results',
    name: 'Test Results',
    description: 'Match each appointment to its lab result via participant.',
    baseKey: 'participant',
    joinedKey: 'participant',
  },
  {
    id: 'appts-surveys',
    baseId: 'appointments',
    joinedId: 'surveys',
    name: 'Participant Surveys',
    description:
      'Attach post-appointment satisfaction ratings via participant.',
    baseKey: 'participant',
    joinedKey: 'participant',
  },
  {
    id: 'appts-clearances',
    baseId: 'appointments',
    joinedId: 'clearances',
    name: 'Preemptive Clearances',
    description: 'Attach clearance requests and status via participant.',
    baseKey: 'participant',
    joinedKey: 'participant',
  },
  {
    id: 'results-surveys',
    baseId: 'results',
    joinedId: 'surveys',
    name: 'Participant Surveys',
    description: 'Attach satisfaction ratings to each result via participant.',
    baseKey: 'participant',
    joinedKey: 'participant',
  },
]

export function getJoin(id: string): JoinDef | undefined {
  return joins.find((j) => j.id === id)
}

export function joinsForDataset(baseId: string): JoinDef[] {
  return joins.filter((j) => j.baseId === baseId)
}

/** Namespaced key for a joined field, e.g. "results.turnaroundHrs" */
export function joinedFieldKey(join: JoinDef, fieldKey: string): string {
  return `${join.joinedId}.${fieldKey}`
}

/**
 * Merged field list for a base dataset + optional join.
 * Joined fields are namespaced ("results.lab") and labeled with their
 * source ("Results · Laboratory") so name collisions are impossible.
 */
export function getMergedFields(
  baseId: string,
  joinId: string | null | undefined,
): DatasetField[] {
  const base = getDataset(baseId)
  if (!base) return []
  if (!joinId) return base.fields
  const join = getJoin(joinId)
  if (!join || join.baseId !== baseId) return base.fields
  const joined = getDataset(join.joinedId)
  if (!joined) return base.fields
  const joinedFields: DatasetField[] = joined.fields
    .filter((f) => f.key !== join.joinedKey) // skip the duplicate join key
    .map((f) => ({
      key: joinedFieldKey(join, f.key),
      label: `${joined.name} · ${f.label}`,
      type: f.type,
    }))
  return [...base.fields, ...joinedFields]
}

/**
 * LEFT-join the base rows with the joined dataset. One-to-many matches
 * produce one row per match (the classic BI fan-out, kept intentionally
 * visible for the prototype). Unmatched rows keep base data with empty
 * joined fields.
 */
export function buildJoinedRows(
  baseId: string,
  joinId: string,
): Record<string, string | number>[] {
  const base = getDataset(baseId)
  const join = getJoin(joinId)
  if (!base || !join || join.baseId !== baseId) return base?.rows ?? []
  const joined = getDataset(join.joinedId)
  if (!joined) return base.rows

  const index = new Map<string, Record<string, string | number>[]>()
  for (const row of joined.rows) {
    const key = String(row[join.joinedKey] ?? '')
    const list = index.get(key) ?? []
    list.push(row)
    index.set(key, list)
  }

  const out: Record<string, string | number>[] = []
  for (const baseRow of base.rows) {
    const matches = index.get(String(baseRow[join.baseKey] ?? '')) ?? []
    if (matches.length === 0) {
      out.push({ ...baseRow })
      continue
    }
    for (const match of matches) {
      const row: Record<string, string | number> = { ...baseRow }
      for (const f of joined.fields) {
        if (f.key === join.joinedKey) continue
        row[joinedFieldKey(join, f.key)] = match[f.key]
      }
      out.push(row)
    }
  }
  return out
}
